/** @jsx jsx */

import { Fragment as _, useContext, useEffect } from 'react';
import { css as _css } from 'emotion';
import { jsx, css } from '@emotion/core';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import getBlockById from 'services/apiHelpers/getBlockById';
import { interactionContext } from './interactionContext';
import { Box } from 'theme-ui';
import { useSelector } from 'react-redux';
import { contentCard_default } from 'factories/interactions/contentcard';
import hexToRgb, { getHexToRgbWithDefaultFallback } from 'services/hexToRGB';
import { position } from 'html2canvas/dist/types/css/property-descriptors/position';
import { backgroundPosition } from 'html2canvas/dist/types/css/property-descriptors/background-position';
import { backgroundSize } from 'html2canvas/dist/types/css/property-descriptors/background-size';
import { imgPlaceholders } from 'components/lib/Image';
import { visibility } from 'html2canvas/dist/types/css/property-descriptors/visibility';
import { opacity } from 'html2canvas/dist/types/css/property-descriptors/opacity';

const Text = props => {
  const { sx = {}, children } = props;

  return (
    <Box className="text" sx={sx}>
      {children}
    </Box>
  );
};

const ButtonWrapper = ({ sx = {}, children }) => {
  return (
    <Box
      className="btn"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        margin: '15px 0 20px 0',
        padding: '0 10px',

        a: {
          minWidth: 'calc(100% - 20px)',
        },

        ...(window.dev() && {
          '> div': {
            //
            // so the editWrapper behaves
            minWidth: '100%',
          },
        }),
        //
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const ContentCard = ({ children, ...props }) => {
  //

  let [media, text, btn] = children;

  // the order these are in from the factory :
  // Text
  // Image
  // Video
  // SVG
  // Embed
  // Audio
  // PDF
  const medias = ['text', 'image', 'video', 'svg', 'embed', 'audio', 'pdf'];
  const mediaIndex = media?.props?.child;

  const context = useContext(interactionContext) as any;

  const { id } = props as any;
  const block = getBlockById(id);

  // get the props directly off the block so updates happen
  const { selected, _props } = useSelector((state: any) => {
    return {
      selected: id === state.focusedNodeId,
      _props: block.props,
    };
  });

  let {
    includeCardBackground = true,
    contentCardBackgroundColor,
    contentcardBackgroundOpacity = 100,
    contentCardPanelTextColor,
    contentCardContentTextColor,
    width,
    height,
    borderRadius,
    outline = false,
    borderColor,
    imagePosition,
    includeText,
    includeButton = true,
    includeShadow,
    gridCell,
    className,
    uniformHeight = false,
  } = {
    ...(context.props as any),
    ...{
      //
      ..._props,
      ...(window.dev && window.dev() && selected && _props),
    },
  } as any;

  const mediaProps = media?.props?.children[mediaIndex]?.props;

  let imgProps;

  //
  if (
    mediaIndex === 1 &&
    ['left', 'right'].includes(imagePosition) &&
    ['cover', 'fill'].includes(mediaProps?.fit)
  ) {
    const { src = imgPlaceholders[0], width, height, fit, imgMagnification } =
      mediaProps || {};

    imgProps = {
      src,
      width,
      height,
      fit,
      imgMagnification,
    };
  }

  let mediaPadding: string | undefined = '0px';

  if (medias[mediaIndex]) {
    mediaPadding = {
      text: window.dev && window.dev() ? '10px' : '20px 20px 0 20px',
      pdf: window.dev && window.dev() ? '15px' : '0px',

      undefined: mediaPadding,
    }[medias[mediaIndex]];
  }

  const textMargin = window.dev && window.dev() ? '10px' : '20px';

  const mediaBorderRadius =
    !includeCardBackground && !outline && !includeShadow
      ? {
          borderRadius,
          overflow: 'hidden',
        }
      : {};

  const { r, g, b } = getHexToRgbWithDefaultFallback(
    contentCardBackgroundColor,
    '#ffffff'
  );

  let a = contentcardBackgroundOpacity / 100;

  if (!contentCardBackgroundColor) {
    a = 0;
  }

  // When 2 columns and image.fit is contain
  // we want to take into account the border radius of the card
  // so as not to have it 'clip' the corners of the image
  // we do this by increasing the padding of the container
  // relative to the borderRadius

  const isImageContained = mediaProps?.fit === 'contain';
  const is2column = ['left', 'right'].includes(imagePosition);

  // default for either not 2 column or image is fit or fill
  let imgMediaPadding: string = '0px';

  if (is2column && isImageContained) {
    if (borderRadius > 20) {
      imgMediaPadding = `${borderRadius / 1.5}px`;
    } else {
      imgMediaPadding = `${20 / 1.5}px`;
    }
  }

  const mediaPaddingWhenFitContain = imgMediaPadding;

  const leftRightPositionMedia = (
    <Box
      className="media"
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,

        padding: mediaPadding || mediaPaddingWhenFitContain,

        ...mediaBorderRadius,

        overflow: 'hidden',

        ...(imgProps && {
          '.chameleon-image > div': {
            background: 'none',
          },
        }),

        '@media (max-width: 768px)': {
          background: 'none',

          img: {
            //
            maxHeight: '50vh !important',
          },
        },
      }}
    >
      {imgProps && (
        <span
          css={css({
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: `url('${imgProps.src}') no-repeat`,
            backgroundPosition: 'center',
            backgroundSize: `${imgProps.fit}`,
            transform: `scale(${imgProps.imgMagnification * 0.01})`,
            transformOrigin: 'center',
            // margin: imgProps.fit === 'contain' ? 10 : 0,
          })}
        />
      )}
      {media}
    </Box>
  );

  return (
    <Box
      id={`card_${id}`}
      className={'card' || className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',

        width: '100%',
        background: includeCardBackground
          ? `rgba(${r}, ${g}, ${b}, ${a})` || contentCardBackgroundColor
          : 'transparent',
        borderRadius,
        ...(includeShadow && {
          boxShadow: '0px 0px 10px rgba(13, 16, 40, 0.15)',
        }),

        ...(outline && {
          border: `1px solid ${borderColor}`,
        }),

        overflow: 'hidden',

        div: {
          color: contentCardContentTextColor,
        },

        '.video-player-container': {
          margin: 0,
        },

        '.media': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
    >
      {imagePosition === 'top' && (
        <_>
          <span>
            <Box
              className="media"
              sx={{
                width: '100%',
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
            {includeText && (
              <Text
                sx={{
                  margin: textMargin,
                }}
              >
                {text}
              </Text>
            )}
          </span>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </_>
      )}

      {imagePosition === 'right' && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            // justifyContent: 'space-between',
            // height: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              // position: 'relative',

              '@media (max-width: 768px)': {
                flexDirection: 'column',
              },
            }}
          >
            {includeText && (
              <Text
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  margin: textMargin,
                }}
              >
                {text}
              </Text>
            )}
            {leftRightPositionMedia}
          </Box>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}

      {imagePosition === 'bottom' && (
        <_>
          <span>
            {includeText && <Text sx={{ margin: textMargin }}>{text}</Text>}
            <Box
              className="media"
              sx={{
                width: '100%',
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
          </span>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </_>
      )}

      {imagePosition === 'left' && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',

              '@media (max-width: 768px)': {
                flexDirection: 'column',
              },
            }}
          >
            {/* <Box
              className="media"
              sx={{
                flex: 1,
                padding: mediaPadding,
                ...mediaBorderRadius,

                '@media (max-width: 768px)': {
                  padding: '20px 0',

                  img: {
                    maxHeight: '50vh !important',
                  },
                },
              }}
            >
              {media}
              {
            </Box> */}
            {leftRightPositionMedia}
            {includeText && (
              <Text
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  margin: textMargin,
                }}
              >
                {text}
              </Text>
            )}
          </Box>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}

      {imagePosition === 'full' && (
        <Box
          className="media"
          sx={{
            width: '100%',
            height: '100%',

            '*': {
              width: '100%',
              height: '100%',
            },
          }}
        >
          {media}
        </Box>
      )}

      {imagePosition === 'none' && (
        <Box>
          <Text sx={{ margin: `20px ${textMargin}` }}>{text}</Text>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}
    </Box>
  );
};

export default ContentCard;
